import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import {
	DropdownMenuItem,
	DropdownMenuSeparator
} from '@/components/DropdownMenu';
import ShareDocumentModal from '@/components/ShareDocumentModal';
import PrintDocumentContent from '@/components/ShareDocumentModal/PrintDocumentContent';
import { useMediaQuery } from '@/hooks';
import Remove from '@/icons/delete.svg';
import { SHARE_MODAL_TYPES } from '@/store/editor';
import { deleteDocuments } from '@/store/myDocuments/thunks';
import { cn, showError, showSuccess } from '@/utils';

export function RemoveDocument({ open, onOpenChange, selectedTransactions }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const fileDownloading = useSelector(
		state => state.myDocuments.deleteDocuments.loading
	);

	const handleRemoveDocuments = async () => {
		const ids = selectedTransactions.map(tx => tx.id);

		dispatch(deleteDocuments({ ids }))
			.unwrap()
			.then(() => {
				onOpenChange(false);
				showSuccess(t('myDocuments.toast.removeDocumentSuccess'));
			})
			.catch(showError);
	};

	const title =
		selectedTransactions?.length > 1
			? t('myDocuments.modals.removeDocument.titlePlural')
			: t('myDocuments.modals.removeDocument.titleSingular');
	const description =
		selectedTransactions?.length > 1
			? t('myDocuments.modals.removeDocument.descriptionPlural')
			: t('myDocuments.modals.removeDocument.descriptionSingular');
	const text =
		selectedTransactions?.length > 1
			? t('myDocuments.modals.removeDocument.textPlural')
			: t('myDocuments.modals.removeDocument.textSingular');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop title={title} description={description} />

				<div className="flex flex-col items-center gap-2 px-4 py-9">
					<Remove className="h-5 w-5 text-red" />
					<p className="whitespace-pre-line text-center text-sm text-grey-500">
						{text}
					</p>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button
						onClick={handleRemoveDocuments}
						loading={fileDownloading}
					>
						{t('common.delete')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function PrintDocument({
	open,
	onOpenChange,
	selectedTransactions,
	id
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<PrintDocumentContent
					id={id}
					onOpenChange={onOpenChange}
					selectedTransactions={selectedTransactions}
				/>
			</DialogContent>
		</Dialog>
	);
}

export function ShareDocument({ open, onOpenChange, selectedTransactions }) {
	const id = selectedTransactions?.[0]?.id || '';
	const publicId = selectedTransactions?.[0]?.publicId || '';

	return (
		<ShareDocumentModal
			defaultFileName={selectedTransactions[0].metadata?.originalFileName}
			id={id}
			onOpenChange={onOpenChange}
			open={open}
			type={SHARE_MODAL_TYPES.COPY_LINK}
			publicId={publicId}
		/>
	);
}

export function DialogItem({ options, setOpenOption }) {
	const mdDown = useMediaQuery('mdDown');

	return (
		<>
			{options.map(({ Icon, label, name }) => {
				return (
					<div key={name}>
						{name === 'delete' && <DropdownMenuSeparator />}
						{mdDown ? (
							<div
								className={cn(
									'flex gap-2 py-1 text-grey-500 hover:cursor-pointer',
									mdDown && 'text-xs leading-5'
								)}
								onClick={() => {
									setOpenOption(name);
								}}
							>
								<Icon />
								{label}
							</div>
						) : (
							<DropdownMenuItem
								onClick={() => {
									setOpenOption(name);
								}}
							>
								<Icon />
								{label}
							</DropdownMenuItem>
						)}
					</div>
				);
			})}
		</>
	);
}
